/**
 * Documents and opportunities — ADR-001 §7.3 (`documents`, `document_versions`, `opportunities`), §7.6 ("Documents
 * are the Approvals board": `stage_since`, `last_sent_note`; `opportunities.value numeric NULL` with
 * `value_unknown`), §11.10; guide §4 ("Document: client, name, type, version, approval stage"; "Opportunity:
 * client, title, type, value, spotted by, stage"); the prototype's DOCS / APPROVALS / moveAp / OPPS / moveOpp
 * (docs/prototype.html L503–512, L555–571, L1052–1076, L1322–1339).
 *
 * The prototype keeps two lists — a file store (DOCS) and a stage board (APPROVALS). The guide and the ADR make
 * them ONE Document: a client's MIS, deck, report, RCA or plan, its file versions, and where it is on the way to
 * client sign-off. The Documents tab and the Approvals board are two views of the same rows.
 *
 * Visibility follows the client: managers all; an ops member the clients they are the current SPOC of (others
 * 404). Writes: a manager, or the client's SPOC. An opportunity's value is revenue: `value_lakh` is ABSENT (not
 * null) for everyone without `revenue.read`, and only they may set it. Every write is audited.
 */
import { z } from 'zod';
import { IsoDate, Lakh, PageQuery, Timestamp, Uuid, Version, page } from './common.js';
import { ClientRef } from './clients.js';
import { UserRef } from './users.js';
/** A storage key from `POST /documents/sign-upload`, bound to the caller (another person's key is `422`). */
const FileKey = z.string().min(1).max(500);
const Name = z.string().trim().min(2, 'Give it a name of at least 2 characters').max(160, 'Keep the name under 160 characters');
const Note = z.string().trim().min(1).max(500);
// ============================================================================ documents
export const DocType = z.enum(['MIS', 'Deck', 'Report', 'RCA', 'Plan']).meta({ id: 'DocType' });
/** Prototype STAGES (ADR §7.3 names). Any move is allowed, as in the prototype; moving to the same stage is `409`. */
export const DocStage = z
    .enum(['drafting', 'internal_review', 'with_client', 'changes_requested', 'approved'])
    .meta({ id: 'DocStage' });
export const DOC_STAGE_LABEL = {
    drafting: 'Drafting',
    internal_review: 'Internal review',
    with_client: 'With client',
    changes_requested: 'Changes requested',
    approved: 'Approved',
};
/** "With client" for this many days or more is stuck (prototype `stage==='with' && days>=3`). */
export const DOC_STUCK_DAYS = 3;
export const DocumentVersion = z
    .object({
    id: Uuid,
    /** v1, v2 … — the chip "Version N". */
    revision: z.number().int().positive(),
    /** `null` for a revision opened by "Changes requested" before its file is uploaded. */
    filename: z.string().nullable(),
    bytes: z.number().int().nonnegative().nullable(),
    has_file: z.boolean(),
    by: UserRef,
    at: Timestamp,
    /** e.g. "Revised after feedback", or what the uploader wrote. */
    note: z.string().nullable(),
})
    .meta({ id: 'DocumentVersion' });
const DocumentBase = z.object({
    id: Uuid,
    client: ClientRef,
    name: z.string(),
    doc_type: DocType,
    stage: DocStage,
    /** The current revision (v1, v2 …). Moving to `changes_requested` opens the next one (ADR §11.10). */
    revision: z.number().int().positive(),
    /** The day it entered its stage; with `days_in_stage` gives the "N days in stage" chip. */
    stage_since: IsoDate,
    days_in_stage: z.number().int().nonnegative(),
    /** With the client for `DOC_STUCK_DAYS` days or more. */
    is_stuck: z.boolean(),
    owner: UserRef,
    /** The board's "Latest email" line, e.g. "Sent 1 Oct, no reply" — typed by a person. */
    last_sent_note: z.string().nullable(),
    /** The current revision has a file to open. */
    has_file: z.boolean(),
    can_edit: z.boolean(),
    /** Optimistic-concurrency version (If-Match), bumped on every write — not the document revision. */
    version: Version,
    created_at: Timestamp,
    updated_at: Timestamp,
});
export const DocumentListItem = DocumentBase.meta({ id: 'DocumentListItem' });
/** The drawer: with the version history, newest first. */
export const Document = DocumentBase.extend({ versions: z.array(DocumentVersion) }).meta({ id: 'Document' });
export const ListDocumentsQuery = PageQuery.extend({
    client_id: Uuid.optional(),
    doc_type: DocType.optional(),
    stage: DocStage.optional(),
    /** Managers' "View as: Mine" on the board. */
    owner: z.enum(['me', 'all']).default('all'),
    /** `true` = with the client for 3 days or more. */
    stuck: z.enum(['true', 'false']).transform((s) => s === 'true').optional(),
}).strict();
/** The board's stats, within what the caller sees and the filters' client. */
export const DocumentStats = z
    .object({
    in_flight: z.number().int().nonnegative(),
    with_client: z.number().int().nonnegative(),
    with_client_stuck: z.number().int().nonnegative(),
    changes_requested: z.number().int().nonnegative(),
    approved_this_week: z.number().int().nonnegative(),
})
    .meta({ id: 'DocumentStats' });
export const DocumentPage = page(DocumentListItem).extend({ stats: DocumentStats }).meta({ id: 'DocumentPage' });
export const DOCUMENT_MAX_BYTES = 50 * 1024 * 1024;
export const SignDocumentUploadBody = z
    .object({
    filename: z.string().trim().min(1).max(200),
    bytes: z.number().int().positive().max(DOCUMENT_MAX_BYTES, 'The file is over 50 MB. Compress it or split it.'),
    content_type: z.string().trim().toLowerCase().max(120),
})
    .strict();
/**
 * POST /documents — "Upload a file" (with `file_key`, starts as v1) or "New item for approval" (no file yet).
 * It starts in Drafting unless `stage` says otherwise. The owner is the caller (a manager may name another Ops
 * person). The name defaults to nothing — the SPA fills it from the file name as the prototype does.
 */
export const CreateDocumentBody = z
    .object({
    client_id: Uuid,
    name: Name,
    doc_type: DocType,
    file_key: FileKey.optional(),
    stage: DocStage.default('drafting'),
    owner_id: Uuid.optional(),
    note: Note.optional(),
})
    .strict();
export const UpdateDocumentBody = z
    .object({
    name: Name.optional(),
    doc_type: DocType.optional(),
    /** An active Ops person; a member may hand it only to themselves or the client's SPOC. */
    owner_id: Uuid.optional(),
    last_sent_note: z.string().trim().max(300).nullable().optional(),
})
    .strict()
    .refine((b) => Object.values(b).some((v) => v !== undefined), { message: 'Change at least one thing' });
/**
 * POST /documents/{id}/versions — attach a file. It fills the current revision when that has no file yet (the
 * revision "Changes requested" opened); otherwise it opens the next revision.
 */
export const AddDocumentVersionBody = z.object({ file_key: FileKey, note: Note.optional() }).strict();
/** POST /documents/{id}/stage — "Move to". `note` updates the "Latest email" line when given. */
export const MoveDocumentBody = z
    .object({ stage: DocStage, note: z.string().trim().max(300).optional() })
    .strict();
export const DocumentVersionParams = z.object({ id: Uuid, vid: Uuid });
// ============================================================================ opportunities
export const OppType = z.enum(['Upsell', 'Cross-sell', 'Referral']).meta({ id: 'OppType' });
/** Prototype OSTAGES (ADR §7.3 names). */
export const OppStage = z.enum(['spotted', 'discussed', 'handed_to_sales', 'closed']).meta({ id: 'OppStage' });
export const OPP_STAGE_LABEL = {
    spotted: 'Spotted',
    discussed: 'Discussed with client',
    handed_to_sales: 'Handed to sales',
    closed: 'Closed',
};
const OpportunityBase = z.object({
    id: Uuid,
    client: ClientRef,
    /** "What did you notice". */
    title: z.string(),
    opp_type: OppType,
    stage: OppStage,
    stage_since: IsoDate,
    /** "Spotted by". */
    by: UserRef,
    /** The sales person it went to (stage `handed_to_sales` onwards), when named. */
    handed_to: UserRef.nullable(),
    /** No value estimated yet (prototype "Unknown"). Not revenue, so everyone sees it. */
    value_unknown: z.boolean(),
    can_edit: z.boolean(),
    version: Version,
    created_at: Timestamp,
    updated_at: Timestamp,
});
/** As a member sees it: no value field at all. */
export const OpportunityMember = OpportunityBase.strict().meta({ id: 'OpportunityMember' });
/** As the founder and the associate director see it: the estimated value, INR lakh per month. */
export const OpportunityManager = OpportunityBase.extend({ value_lakh: Lakh.nullable() }).strict().meta({ id: 'OpportunityManager' });
export const Opportunity = z.union([OpportunityManager, OpportunityMember]).meta({ id: 'Opportunity' });
export const ListOpportunitiesQuery = PageQuery.extend({
    client_id: Uuid.optional(),
    stage: OppStage.optional(),
    opp_type: OppType.optional(),
}).strict();
/** Prototype stats: "Spotted" (all of them), "With sales", "Closed" — counts only, never value. */
export const OpportunityStats = z
    .object({ total: z.number().int().nonnegative(), with_sales: z.number().int().nonnegative(), closed: z.number().int().nonnegative() })
    .meta({ id: 'OpportunityStats' });
export const OpportunityPageManager = page(OpportunityManager).extend({ stats: OpportunityStats }).meta({ id: 'OpportunityPageManager' });
export const OpportunityPageMember = page(OpportunityMember).extend({ stats: OpportunityStats }).meta({ id: 'OpportunityPageMember' });
export const OpportunityPage = z.union([OpportunityPageManager, OpportunityPageMember]).meta({ id: 'OpportunityPage' });
/**
 * POST /opportunities — "Add an opportunity" starts in Spotted. `value_lakh` / `value_unknown` only from callers
 * with `revenue.read` (`403` otherwise); without a value it is `value_unknown: true`.
 */
export const CreateOpportunityBody = z
    .object({
    client_id: Uuid,
    title: z.string().trim().min(3, 'Describe it').max(200, 'Keep it under 200 characters'),
    opp_type: OppType,
    value_lakh: Lakh.max(10_000).optional(),
    value_unknown: z.boolean().optional(),
})
    .strict()
    .refine((b) => !(b.value_lakh !== undefined && b.value_unknown === true), {
    path: ['value_unknown'],
    message: 'Give a value or mark it unknown, not both',
});
/** Edit the card. The value fields need `revenue.read` (`403`). */
export const UpdateOpportunityBody = z
    .object({
    title: z.string().trim().min(3, 'Describe it').max(200).optional(),
    opp_type: OppType.optional(),
    value_lakh: Lakh.max(10_000).nullable().optional(),
    value_unknown: z.boolean().optional(),
})
    .strict()
    .refine((b) => Object.values(b).some((v) => v !== undefined), { message: 'Change at least one thing' });
/**
 * POST /opportunities/{id}/stage — "Move to". `handed_to_id` (an active sales person) may be given when moving to
 * `handed_to_sales`; it is `422` with any other stage. Moving to the same stage is `409`.
 */
export const MoveOpportunityBody = z
    .object({ stage: OppStage, handed_to_id: Uuid.optional() })
    .strict()
    .refine((b) => b.handed_to_id === undefined || b.stage === 'handed_to_sales', {
    path: ['handed_to_id'],
    message: 'Name the sales person only when handing it to sales',
});
//# sourceMappingURL=documents.js.map